import { empty, intval, strval, trim, strcasecmp, sort, ksort, SORT_STRING, json_encode,
    JSON_UNESCAPED_UNICODE, JSON_UNESCAPED_SLASHES, implode, phpCopy, PhpArray, isset, strtolower } from '../../php-compat';
import type { PortEnv } from '../../port-contract';
import { portSqlFingerprint } from '../../port-contract';
import { slot, array, values, isArray, type Arr } from './access';
import { skillId, catalogVariableName, catalogRows, catalogId } from './catalog';
import { setPath, appendPath } from './access';
import { floatval, ceil, round } from '../../php-compat';
import type { PhpRef } from '../../port-contract';
export interface SkillListDeps {
    pl_cost_layer_entry_uuid(entry: unknown): string;
    check_uuid(entry: unknown): boolean;
    item_arr_scalar_layer_slot(entry: unknown): string;
}
/** PHP: skill_roll_modifier_layer_entry_matches_skill (includes/functions.php:3178) */
export function skillRollModifierLayerEntryMatchesSkill(entry: unknown, id: string, variable: string, env: PortEnv<SkillListDeps>): boolean {
    if (id === '') return false;
    const resolved = env.deps.pl_cost_layer_entry_uuid(entry);
    if (resolved !== '' && resolved === id) return true;
    if (typeof entry === 'string') {
        const text = trim(entry);
        if (text === '') return false;
        if (env.deps.check_uuid(text)) return text === id;
        if (strcasecmp(text, variable) === 0) return true;
        const resolvedId = skillId(text, env);
        return resolvedId !== null && resolvedId !== '' && resolvedId === id;
    }
    if (!isArray(entry)) return false;
    const name = env.deps.item_arr_scalar_layer_slot(slot(entry, 'variable_name') ?? '');
    return name !== '' && skillRollModifierLayerEntryMatchesSkill(name, id, variable, env);
}
/** PHP: skill_roll_modifier_layer_entry_signature_token (includes/functions.php:3228) */
export function skillRollModifierLayerEntrySignatureToken(entry: unknown, env: PortEnv<SkillListDeps>): string | null {
    if (typeof entry === 'string') { const text = trim(entry); return text === '' ? null : 's:' + text; }
    if (!isArray(entry)) return null;
    const id = env.deps.pl_cost_layer_entry_uuid(entry);
    if (id !== '') return 'u:' + id;
    const variable = env.deps.item_arr_scalar_layer_slot(slot(entry, 'variable_name') ?? '');
    if (variable !== '') return 'v:' + variable;
    const staticKey = 'skill_roll_modifier_layer_entry_signature_token::$normalize';
    const normalize = (env.functionStatics[staticKey] ??= closure3254Normalize) as typeof closure3254Normalize;
    const encoded = json_encode(normalize(entry), JSON_UNESCAPED_UNICODE | JSON_UNESCAPED_SLASHES);
    return encoded !== false ? 'j:' + encoded : null;
}
/** PHP: {closure@3254:15461} (includes/functions.php:3254) */
export function closure3254Normalize(input: Arr): PhpArray {
    const output = new PhpArray();
    for (const [key, value] of array(input)) {
        if (typeof key !== 'string') continue;
        output.set(key, isArray(value) ? closure3254Normalize(value) : phpCopy(value));
    }
    return ksort(output, SORT_STRING);
}
/** PHP: skill_roll_modifier_layer_list_signature_segment (includes/functions.php:3286) */
export function skillRollModifierLayerListSignatureSegment(list: Arr | null, env: PortEnv<SkillListDeps>): string {
    if (list === null || empty(list)) return '-';
    const tokens: string[] = [];
    for (const value of values(list)) {
        const token = skillRollModifierLayerEntrySignatureToken(value, env);
        if (token !== null) tokens.push(token);
    }
    return tokens.length === 0 ? '-' : implode(',', sort(tokens, SORT_STRING));
}
/** PHP: skill_roll_modifier_layer_list_contains_skill (includes/functions.php:3307) */
export function skillRollModifierLayerListContainsSkill(list: Arr | null, id: string, variable: string, env: PortEnv<SkillListDeps>): boolean {
    if (list === null || empty(list)) return false;
    for (const entry of values(list)) if (skillRollModifierLayerEntryMatchesSkill(entry, id, variable, env)) return true;
    return false;
}
/** PHP: count_skill_roll_list_matches_for_sheet_display (includes/functions.php:2052) */
export function countSkillRollListMatchesForSheetDisplay(list: unknown, id: string, env: PortEnv<SkillListDeps>): number {
    if (!isArray(list) || empty(list) || id === '') return 0;
    const variable = catalogVariableName('Skill', id, env) ?? '';
    let count = 0;
    for (const entry of values(list)) if (skillRollModifierLayerEntryMatchesSkill(entry, id, variable, env)) count++;
    return count;
}
export interface RollDefinitionDeps {
    check_uuid(value: unknown): boolean;
    _ft_resolve_carrier_ref(id: string): unknown;
    layer_value(row: Arr, name: string): unknown;
    layer_entries(row: Arr | null, name: string): Arr;
    ft_normalize_layer_entries_to_distinct_uuids(entries: Arr): Arr;
    query_full_item_arr(id: unknown): Arr;
}
/** PHP: find_highest_average_roll_value (includes/functions.php:16643) */
export function findHighestAverageRollValue(effects: Arr, rt: string, env: PortEnv<RollDefinitionDeps>): unknown {
    let highest: number | null = null, best: unknown = null;
    for (const effect of values(effects)) {
        let def: unknown = slot(effect, 'status_arr') ?? slot(effect, 'status_item_id') ?? null;
        if (typeof def === 'string' && def !== '' && env.deps.check_uuid(def)) def = env.deps._ft_resolve_carrier_ref(def);
        if (!isArray(def)) continue;
        if (env.deps.layer_value(def, 'roll_type') === null || env.deps.layer_value(def, 'roll_type') !== rt) continue;
        const die = Number(intval(env.deps.layer_value(def, 'die') ?? 0));
        const dice = Number(intval(env.deps.layer_value(def, 'dice') ?? 0));
        const mod = Number(intval(env.deps.layer_value(def, 'mod') ?? 0));
        if (dice <= 0 || die <= 0) continue;
        const average = dice * ((die + 1) / 2) + mod;
        if (highest === null || average > highest) { highest = average; best = phpCopy(effect); }
    }
    return best;
}
/** PHP: skill_roll_inc_status_roll_type_ids (includes/functions.php:19868) */
export function skillRollIncStatusRollTypeIds(status: Arr | null, name: string, env: PortEnv<RollDefinitionDeps>): Arr {
    return env.deps.ft_normalize_layer_entries_to_distinct_uuids(env.deps.layer_entries(status, name));
}
/** PHP: getMaxSpellLevel (includes/functions.php:20163) */
export function getMaxSpellLevel(spells: Arr, env: PortEnv<RollDefinitionDeps>): unknown {
    let maximum: unknown = 0;
    for (const id of values(spells)) {
        const data = env.deps.query_full_item_arr(id);
        const level = env.deps.layer_value(data, 'spell_level') ?? 0;
        maximum = max(maximum, level);
    }
    return maximum;
}
import { max } from '../../php-compat';
export interface CarrierSkillDeps {
    caster_class_level_map(row: Arr): Arr;
    layer_value(row: Arr, name: string): unknown;
    _ft_default_provider_carriers(row: Arr): Iterable<Arr>;
    _ft_node_item_id(row: unknown): unknown;
    layer_entries(row: Arr, name: string): Arr;
    ability_entry_id(entry: unknown): unknown;
    ft_ability_def_is_level_locked(def: Arr, levels: Arr, total: number, carrier: string): boolean;
    check_uuid(value: unknown): boolean;
    item_field(row: unknown, name: string): unknown;
    _ft_resolve_carrier_ref(id: string): unknown;
    check_ability_conditions(ability: Arr, caster: Arr): boolean;
    contains_trigger_key(ability: Arr): boolean;
}
/** PHP: ft_collect_caster_carrier_skill_roll_sources (includes/functions.php:2080) */
export function ftCollectCasterCarrierSkillRollSources(caster: Arr, env: PortEnv<CarrierSkillDeps>): Arr {
    const sources: Arr[] = [];
    const pc = isset(caster, 'player_characters') && isArray(slot(caster, 'player_characters'));
    const levels = pc ? env.deps.caster_class_level_map(caster) : [];
    const total = pc ? Number(intval(env.deps.layer_value(caster, 'total_level') ?? 0)) : 0;
    const containers = ['feat', 'passive_ability', 'status_effect_permanent'];
    const carrierOf = new Map(containers.map(name => [name, new Map<string, string>()]));
    if (pc) for (const carrier of env.deps._ft_default_provider_carriers(caster)) {
        const node = env.deps._ft_node_item_id(carrier);
        const cid = typeof node === 'string' ? strtolower(node) : '';
        for (const container of containers) for (const entry of values(env.deps.layer_entries(carrier as Arr, container))) {
            const id = strtolower(strval(env.deps.ability_entry_id(entry)));
            const map = carrierOf.get(container)!;
            if (id !== '' && !map.has(id)) map.set(id, cid);
        }
    }
    const locked = (def: Arr, carrier = ''): boolean => closure2112LevelLocked(def, carrier, pc, levels, total, env);
    const rawIds = (container: string): Set<string> => {
        const ids = new Set<string>();
        const raw = slot(caster, container);
        for (const entry of isArray(raw) ? values(raw) : []) {
            if (!isArray(entry)) continue;
            const id = strval(env.deps.item_field(slot(entry, 'item_arr') ?? [], 'item_id') ?? env.deps.ability_entry_id(entry) ?? '');
            if (id !== '') ids.add(id);
        }
        return ids;
    };
    for (const container of containers) {
        const raw = rawIds(container);
        for (let entry of values(env.deps.layer_entries(caster, container))) {
            if (typeof entry === 'string' && env.deps.check_uuid(entry)) entry = { item_id: entry };
            if (!isArray(entry)) continue;
            let ability = slot(entry, 'item_arr') ?? slot(entry, 'item_id') ?? entry;
            if (typeof ability === 'string' && env.deps.check_uuid(ability)) ability = env.deps._ft_resolve_carrier_ref(ability) ?? entry;
            if (!isArray(ability)) continue;
            const id = strval(env.deps.item_field(ability, 'item_id') ?? env.deps.ability_entry_id(entry) ?? '');
            if (id === '' || raw.has(id)) continue;
            const applied = (slot(entry, 'built_from_item_type') ?? '') === 'status_effect';
            if (!applied && locked(ability, carrierOf.get(container)!.get(strtolower(id)) ?? '')) continue;
            if (!env.deps.check_ability_conditions(ability, caster) || env.deps.contains_trigger_key(ability) && !applied) continue;
            const adv = env.deps.layer_entries(ability, 'skill_roll_adv');
            const dis = env.deps.layer_entries(ability, 'skill_roll_dis');
            const mod = env.deps.layer_entries(ability, 'skill_roll_mod');
            if (empty(adv) && empty(dis) && empty(mod)) continue;
            const name = strval(env.deps.item_field(ability, 'item_name') ?? env.deps.layer_value(ability, 'ability_name')
                ?? (container === 'feat' ? 'Feat' : container === 'status_effect_permanent' ? 'Status Effect' : 'Ability'));
            const href = env.deps.item_field(ability, 'item_id') ?? env.deps.layer_value(ability, 'ability_id') ?? null;
            sources.push({ name, kind: container === 'status_effect_permanent' ? 'status_effect' : 'passive_ability',
                href: href !== null && href !== '' ? strval(href) : null,
                adv_list: isArray(adv) ? phpCopy(adv) : [], dis_list: isArray(dis) ? phpCopy(dis) : [], mod_list: isArray(mod) ? phpCopy(mod) : [] });
        }
    }
    const seen = new Set(containers.flatMap(name => [...rawIds(name)]));
    for (const source of sources) if (!empty(slot(source, 'href'))) seen.add(strval(slot(source, 'href')));
    const seenFeats = new Set<string>(), rawFeats = slot(caster, 'feat');
    const feats = [...(isArray(rawFeats) ? values(rawFeats) : []), ...values(env.deps.layer_entries(caster, 'feat'))];
    for (let entry of feats) {
        if (typeof entry === 'string' && env.deps.check_uuid(entry)) entry = { item_id: entry };
        if (!isArray(entry)) continue;
        let feat: unknown = slot(entry, 'item_arr') ?? slot(entry, 'item_id') ?? entry;
        if (typeof feat === 'string' && env.deps.check_uuid(feat)) feat = env.deps._ft_resolve_carrier_ref(feat);
        if (!isArray(feat)) continue;
        const id = strval(env.deps.item_field(feat, 'item_id') ?? '');
        if (id === '' || seenFeats.has(id)) continue;
        seenFeats.add(id);
        if (locked(feat, carrierOf.get('feat')!.get(strtolower(id)) ?? '') || !env.deps.check_ability_conditions(feat, caster)) continue;
        for (const grant of values(env.deps.layer_entries(feat, 'passive_ability'))) {
            let def = slot(grant, 'item_arr') ?? slot(grant, 'item_id') ?? grant;
            if (typeof def === 'string' && env.deps.check_uuid(def)) def = env.deps._ft_resolve_carrier_ref(def);
            if (!isArray(def)) continue;
            const grantId = strval(env.deps.item_field(def, 'item_id') ?? '');
            if (grantId === '' || seen.has(grantId)) continue;
            seen.add(grantId);
            if (locked(def) || !env.deps.check_ability_conditions(def, caster) || env.deps.contains_trigger_key(def)) continue;
            const adv = env.deps.layer_entries(def, 'skill_roll_adv');
            const dis = env.deps.layer_entries(def, 'skill_roll_dis');
            const mod = env.deps.layer_entries(def, 'skill_roll_mod');
            if (empty(adv) && empty(dis) && empty(mod)) continue;
            sources.push({ name: strval(env.deps.item_field(def, 'item_name') ?? 'Ability'), kind: 'passive_ability', href: grantId,
                adv_list: isArray(adv) ? phpCopy(adv) : [], dis_list: isArray(dis) ? phpCopy(dis) : [], mod_list: isArray(mod) ? phpCopy(mod) : [] });
        }
    }
    return sources;
}
/** PHP: {closure@2112:9509} (includes/functions.php:2112) */
export function closure2112LevelLocked(def: Arr, carrier: string, pc: boolean, levels: Arr, total: number, env: PortEnv<CarrierSkillDeps>): boolean {
    return pc && env.deps.ft_ability_def_is_level_locked(def, levels, total, carrier);
}
export interface SpellLevelDeps {
    item_field(row: unknown, name: string): unknown;
    check_uuid(id: unknown): boolean;
}
/** PHP: get_player_character_spell_casting_level (includes/functions.php:28114) */
export function getPlayerCharacterSpellCastingLevel(row: PhpRef<Arr>, env: PortEnv<SpellLevelDeps>): null {
    let full = 0, half = 0;
    for (let n = 1; n <= 20; n++) {
        const node = slot(row.value, 'player_characters', 'class_' + n);
        const id = isArray(node) ? env.deps.item_field(node, 'item_id') ?? slot(node, 0) ?? '' : strval(node ?? '');
        if (id === '' || !env.deps.check_uuid(id)) continue;
        const raw = slot(row.value, 'player_characters', 'class_level_' + n) ?? 0;
        const level = Number(intval(isArray(raw) && array(raw).has(0) ? slot(raw, 0) : raw));
        const info = !empty(id) && env.deps.check_uuid(id) ? [...catalogRows('PlayableClass').values()].find(row => slot(row, 'item_id') === id) ?? null : null;
        if (info === null) continue;
        const type = slot(info, 'caster_type');
        if (type === catalogId('CasterType', 'full_caster', env)) { full += level; continue; }
        if (type === catalogId('CasterType', 'half_caster', env)) half += level;
    }
    setPath(row.value, ['spell_casting_level', 0], floatval(full + Number(ceil(half / 2))));
    return null;
}
export interface PercentageDamageDeps { ucwords_and_space(key: string): string; }
/** PHP: apply_and_log_percentage_damage_modifier (includes/functions.php:30700) */
export function applyAndLogPercentageDamageModifier(rolls: PhpRef<Arr>, index: number, percentage: unknown,
    key: string, symbol: string, source = '', env: PortEnv<PercentageDamageDeps>): null {
    const pct = Number(floatval(percentage));
    if (pct === 0) return null;
    if (!array(rolls.value).has(index)) {
        env.warn('Undefined array key ' + index, 2);
        env.warn('Trying to access array offset on null', 2);
    }
    if (!isset(rolls.value, index, 'total_damage_to_target')) env.warn('Undefined array key "total_damage_to_target"', 2);
    const original = slot(rolls.value, index, 'total_damage_to_target');
    const next = round(Number(floatval(original)) * (1 + pct / 100));
    setPath(rolls.value, [index, 'total_damage_to_target'], next);
    const change = floatval(Number(next) - Number(floatval(original)));
    if (Number(change) === 0) return null;
    let description = env.deps.ucwords_and_space(key);
    if (!empty(source)) description += ' — ' + source;
    appendPath(rolls.value, [index, 'roll_dice_arr'], [symbol + strval(change), description]);
    appendPath(rolls.value, [index, 'modifiers_applied'], { type: 'percentage_damage', key,
        percentage: floatval(percentage), flat_change: change, symbol, description });
    return null;
}
export interface RandomTableDeps {
    random_table_max_range(id: unknown, fingerprint: string): Arr;
    random_table_matching_range(id: unknown, roll: unknown, fingerprint: string): Arr;
    roll_dice_arr(dice: number, die: unknown, mod: number, advantage: number): Arr;
}
/** PHP: roll_random_table_result (includes/functions.php:11817) */
export function rollRandomTableResult(id: unknown, env: PortEnv<RandomTableDeps>): Arr | null {
    let end: unknown = null;
    for (const row of values(env.deps.random_table_max_range(id, portSqlFingerprint(
        'SELECT roll_range_end FROM random_table_ranges WHERE item_id = ? ORDER BY roll_range_end DESC LIMIT 1'
    )))) end = slot(row, 'roll_range_end');
    if (end === null) env.warn('Undefined variable $roll_range_end', 2);
    const dice = env.deps.roll_dice_arr(1, end, 0, 0);
    const result = slot(dice, 0, 0);
    let output: Arr | null = null;
    for (const row of values(env.deps.random_table_matching_range(id, result, portSqlFingerprint(
        'SELECT * FROM random_table_ranges WHERE item_id = ? AND ? BETWEEN `roll_range_begin` AND `roll_range_end` ORDER BY roll_range_end DESC LIMIT 1'
    )))) {
        const layers = new PhpArray();
        for (const name of ['random_table_range_i', 'random_table_range_t', 'random_table_range_t_updated',
            'random_table_range_private', 'random_table_range_deleted', 'random_table_range_created_by_id',
            'roll_range_begin', 'roll_range_end', 'roll_result_effect', 'roll_result_effect_description',
            'roll_dice', 'roll_die', 'roll_mod', 'item_id_attached']) layers.set(name, slot(row, name));
        output = { item_layer_types: layers, item_id: slot(row, 'item_id') };
    }
    if (output === null) env.warn('Undefined variable $random_table_range_arr', 2);
    return output;
}
